import { useState, useMemo, useEffect, useContext } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButtonLink from '../components/buttons/RoundedButtonLink'
import DropdownMenu from '../components/menu/DropdownMenu'
import FormInput from '../components/inputs/FormInput'
import MenuItem from '@mui/material/MenuItem'
import EntitiesTable from '../components/tables/EntitiesTable'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import EmptyListView from '../components/views/EmptyListView'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import strings from '../res/strings'
import actions from '../assets/actions.png'
import detailsIcon from '../assets/details-icon.png'
import editIcon from '../assets/edit-icon.png'
import enableIcon from '../assets/enable-icon.png'
import disableIcon from '../assets/disable-icon.png'
import deleteIcon from '../assets/delete-icon.png'
import noSchedules from '../assets/no-schedules.png'
import 'react-toastify/dist/ReactToastify.css'

const EntitiesBO = () => {
    const [entities, setEntities] = useState([])

    const [selectedEntity, setSelectedEntity] = useState(null)
    const [filterText, setFilterText] = useState('')

    const [showDisableEntityModal, setShowDisableEntityModal] = useState(false)
    const [showEnableEntityModal, setShowEnableEntityModal] = useState(false)
    const [showRemoveEntityModal, setShowRemoveEntityModal] = useState(false)

    const [anchorElActions, setAnchorElActions] = useState(null)
    const openActions = Boolean(anchorElActions)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.ADMINIT) {
                navigate('/')
            } else {
                getEntities()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get entities request
    const getEntities = () => {
        fetch('/api/Backoffice/ListEntities', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
                  
                    setEntities(jsonRes)
                } else {
                    showErrorToast(strings.getEntitiesError)
                    setEntities([])
                }
            }, 1000)

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
                
                setEntities([])
            }, 1000)
        })
    }

    const columns = useMemo(() => [
        {
            name: strings.name2,
            selector: row => row.entity.name,
            cell: row => row.entity.name,
            sortable: true
        },
        {
            name: strings.nif,
            selector: row => row.entity.nif,
            sortable: true
        },
        {
            name: strings.harbors2,
            selector: (row) => row.harbors.join(', '),
            cell: (row) => <p>{row.harbors.map((harbor, index) => (index === row.harbors.length - 1 ? harbor : `${harbor}; `))}</p>
        },
        {
            name: strings.state,
            selector: row => row.entity.active ? strings.active : strings.inactive,
            sortable: true
        },
        {
            name: strings.startDateConcession,
            selector: row => formatDate(row.entity.date),
            sortable: true
        },
        {
            name: strings.concession,
            selector: row => row.entity.contractor,
            cell: row => row.entity.contractor,
            sortable: true
        },
        {
            name: strings.actions,
            cell: row => actionCell(row.entity),
            button: true
        }
    ], [])

    // Create actions menu
    const actionCell = (row) => {
        return (
            <div className='flex flex-row items-center'>
                <button onClick={(event) => openActionsDropdownMenu(event, row)}>
                    <img className='h-6 w-6' src={actions}  alt='Expandir menu de ações' />
                </button>
            </div>
        )
    }

    // Handle disabled entities UI
    const conditionalRowStyles = [
        {
          when: row => row.entity.active === false,
          style: {
            color: '#A9ACAF'
          }
        }
    ]

    // Handle changes in search input text
    const onChangeSearch = (searchText) => {
        setFilterText(searchText.target.value)
    }

    // Handles date formatting
    const formatDate = (timeStamp) => {
        const date = new Date(timeStamp)
        const day = date.getDate()
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`
    }

    // Handles open actions dropdown menu
    const openActionsDropdownMenu = (event, row) => {
        setAnchorElActions(event.currentTarget)
        setSelectedEntity(row)
    }

    // Handles close actions dropdown menu
    const closeActionsDropdownMenu = () => {
        setAnchorElActions(null)
        setSelectedEntity(null)
    }

    // Navigate to entity details
    const onClickDetails = () => {
        setAnchorElActions(null)
        navigate(`/entity-details/${selectedEntity.id}`)
    }

    // Navigate to update entity
    const onClickUpdate = () => {
        setAnchorElActions(null)
  
        navigate(`/edit-entity/${selectedEntity.id}`)
    }

    // Show disable entity modal
    const onClickDisable = () => {
        setAnchorElActions(null)
        setShowDisableEntityModal(true)
    }

    // Handles disabled entity
    const disableEntity = () => {
        setShowDisableEntityModal(false)
        setIsLoading(true)

        // Disable entity
        fetch(`/api/BackOffice/${selectedEntity.id}/DeactivateEntity`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
    
                if (response.status === 200) {
                    setIsLoading(true)
                    getEntities()
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else if (response.status === 401) {
                    showErrorToast(strings.notAuthorizedEntity)
                } else {
                    showErrorToast(strings.disableEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })  
    }

    // Show enable entity modal
    const onClickEnable = () => {
        setAnchorElActions(null)
        setShowEnableEntityModal(true)
    }

    // Handles enable entity
    const enableEntity = () => {
        setShowEnableEntityModal(false)
        setIsLoading(true)

        // Enable entity
        fetch(`/api/BackOffice/${selectedEntity.id}/ActivateEntity`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
        
                if (response.status === 200) {
                    setIsLoading(true)
                    getEntities()
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else if (response.status === 401) {
                    showErrorToast(strings.notAuthorizedEntity)
                } else {
                    showErrorToast(strings.enableEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })
    }

    // Show remove entity modal
    const onClickRemove = () => {
        setAnchorElActions(null)
        setShowRemoveEntityModal(true)
    }

    // Handles remove entity
    const removeEntity = () => {
        setShowRemoveEntityModal(false)
        setIsLoading(true)

        // Remove user
        fetch(`/api/BackOffice/${selectedEntity.id}/RemoveEntity`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
        
                if (response.status === 200) {
                    setIsLoading(true)
                    getEntities()
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else {
                    showErrorToast(strings.deleteEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }
   
    return (
        <>
            <TopNavBar />

            <div className='h-14 border-b border-accent flex flex-row items-center justify-between px-[12.5rem]'>
                <div className='flex flex-row space-x-9'>
                    <Link to='/backoffice-schedules' className='font-montserrat text-neutral5 cursor-pointer'>{strings.schedules}</Link>
                    <Link to='/backoffice-users' className='font-montserrat text-neutral5 cursor-pointer'>{strings.userManagement}</Link>
                    <p className='font-montserrat font-semibold text-accent'>{strings.entityManagement}</p>
                </div>

                <div className='flex w-[12.875rem]'>
                    <RoundedButtonLink 
                        path={'/new-entity'}
                        text={strings.addEntity}
                        height={40}
                        bgColor='#007EB4'
                        flex={true}
                    />
                </div>
            </div>

            <div className='flex px-[12.5rem] my-8'>
                <div className='flex flex-1 flex-col'>
                    <label htmlFor='filter' className='font-montserrat font-semibold text-sm text-navy'>{strings.search}</label>
                    <FormInput 
                        id='filter' 
                        type='text'
                        value={filterText}
                        placeholder={strings.enterSearchTerms}
                        onChange={onChangeSearch}
                    />
                </div>
            </div>

            <div className='min-h-screen px-[12.5rem] mb-16'>
                { isLoading ?
                    <LoadingModal />
                :   <>
                        { entities.length > 0 ?
                            <EntitiesTable
                                columns={columns}
                                data={entities.filter(
                                    item => (item.entity.name && item.entity.name.toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.entity.nif && item.entity.nif.toString().toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.harbors && item.harbors.some((harbor => harbor.toLowerCase().includes(filterText.toLowerCase()))))
                                        || (item.entity.date && formatDate(item.entity.date).toLowerCase().includes(filterText.toLowerCase()))
                                        || (item.entity.contractor && item.entity.contractor.toLowerCase().includes(filterText.toLowerCase()))
                                )}
                                conditionalRowStyles={conditionalRowStyles}
                            />
                        :   <EmptyListView 
                                image={noSchedules}
                                message={strings.noEntities}
                                path='/new-entity'
                                buttonText={strings.addEntity}
                            />
                        }
                    </>
                }

                
            </div>

            {selectedEntity ?
                <DropdownMenu
                    anchorEl={anchorElActions}
                    open={openActions}
                    onClose={closeActionsDropdownMenu}
                >
                    <MenuItem onClick={onClickDetails} disableRipple>
                        <img className='h-[1.125rem] w-[1.125] me-3' src={detailsIcon} alt='Ver detalhes' />
                        {strings.seeDetails}
                    </MenuItem>

                    {!selectedEntity.active ? null
                    :   <MenuItem onClick={onClickUpdate} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={editIcon} alt='Editar utilizador' />
                            {strings.updateEntity}
                        </MenuItem>
                    }     

                    {!selectedEntity.active ?
                        <MenuItem onClick={onClickEnable} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={enableIcon} alt='Ativar utilizador' />
                            {strings.enableEntity}
                        </MenuItem>
                    :   <MenuItem onClick={onClickDisable} disableRipple>
                            <img className='h-[1.125rem] w-[1.125] me-3' src={disableIcon} alt='Desativar utilizador' />
                            {strings.disableEntity}
                        </MenuItem>
                    }
            
                    <MenuItem onClick={onClickRemove} disableRipple style={{ color: '#E85F5C' }}>
                        <img className='h-[1.125rem] w-[1.125] me-3' src={deleteIcon} alt='Remover utilizador' />
                        {strings.removeEntity}
                    </MenuItem>
                    
                </DropdownMenu>
            : null }

            <TwoButtonsModal 
                open={showDisableEntityModal}
                onClose={() => setShowDisableEntityModal(false)}
                title={strings.disableEntity}
                message={strings.disableEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.disable}
                onClick={disableEntity}
            />

            <TwoButtonsModal 
                open={showEnableEntityModal}
                onClose={() => setShowEnableEntityModal(false)}
                title={strings.enableEntity}
                message={strings.enableEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.enable}
                onClick={enableEntity}
            />

            <TwoButtonsModal 
                open={showRemoveEntityModal}
                onClose={() => setShowRemoveEntityModal(false)}
                title={strings.removeEntity}
                message={strings.removeEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.remove}
                onClick={removeEntity}
            />

            <ToastContainer />
        </>
    )
}

export default EntitiesBO